import { receivablesService } from '@/api/receivables.service';
import type {
  ReceivablesControllerGetDownpaymentsData,
  ReceivablesControllerGetItemsData,
  ReceivablesControllerGetWriteOffsData,
} from '@carvia/ros-client-types';
import { useMutation, useQueries, useQuery } from '@tanstack/vue-query';
import { computed, type MaybeRefOrGetter, toRef, toValue } from 'vue';

const QUERY_KEY = 'receivables';

export const useReceivablesSummary = (
  query: MaybeRefOrGetter<
    Pick<
      NonNullable<ReceivablesControllerGetItemsData['query']>,
      'invoiceTypes' | 'hasPeriodicInvoicing' | 'isDeposit' | 'search' | 'legalEntityId'
    >
  >,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'summary', toRef(query)],
    queryFn: () => receivablesService.getSummary(toValue(query)),
    enabled: computed(() => toValue(enabled)),
  });
};

export const useReceivablesItems = (
  query: MaybeRefOrGetter<ReceivablesControllerGetItemsData['query']>,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'items', toRef(query)],
    queryFn: () => receivablesService.getItems(toValue(query)),
    enabled: computed(() => typeof toValue(query)?.dueDays === 'number'),
  });
};

export const useReceivablesProblems = (
  legalEntityId: MaybeRefOrGetter<string | null | undefined> = undefined,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'problems', toRef(legalEntityId)],
    queryFn: () => receivablesService.getProblems(toValue(legalEntityId) ?? undefined),
    enabled: computed(() => toValue(enabled)),
  });
};

export const useReceivablesDownpayments = (
  query?: MaybeRefOrGetter<ReceivablesControllerGetDownpaymentsData['query']>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'downpayments', toRef(query ?? {})],
    queryFn: () => receivablesService.getDownpayments(toValue(query)),
    enabled: computed(() => toValue(enabled)),
  });
};

export const useReceivablesWriteOffs = (
  query: MaybeRefOrGetter<ReceivablesControllerGetWriteOffsData['query']>,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'writeOffs', toRef(query)],
    queryFn: () => receivablesService.getWriteOffs(toValue(query)),
  });
};

export const useExportReceivablesWriteOffsCsv = () => {
  return useMutation({
    mutationKey: [QUERY_KEY, 'writeOffs', 'export'],
    mutationFn: (query: ReceivablesControllerGetWriteOffsData['query']) =>
      receivablesService.exportWriteOffsCsv(query),
  });
};
